import { useTranslation } from "react-i18next";

import { POUS } from "../framework/data";
import type { ReportAggregates } from "./reportTypes";
import { SERVICE_CONTRIBUTION } from "./serviceContribution";
import { useLabels } from "./useLabels";
import { pct } from "./format";

/**
 * Services view — the service-contribution matrix. For each subdomain (grouped by
 * Pou) it shows the Theory of Change outcome, the current exit protective share,
 * and the NASH services mapped as contributing to that outcome. Mirrors the
 * Services / Service-contribution column of the source workbook.
 *
 * The mapping is working reference content (serviceContribution.ts) for Sue and
 * kaimahi to curate; it is not whānau data.
 */
type Props = { agg: ReportAggregates };

export function ServicesView({ agg }: Props) {
  const { t } = useTranslation();
  const labels = useLabels();

  return (
    <div className="rpt-view">
      <section className="rpt-panel">
        <h2 className="rpt-panel__title">{t("reports.services.title")}</h2>
        <p className="rpt-panel__lead">{t("reports.services.lead")}</p>
        <p className="rpt-panel__desc">{t("reports.services.curationNote")}</p>
      </section>

      {POUS.map((pou) => {
        const subs = agg.bySubdomain.filter((s) => s.pouId === pou.id);
        if (subs.length === 0) return null;
        return (
          <section key={pou.id} className="rpt-panel">
            <h2 className="rpt-panel__title rpt-panel__title--pou" lang="mi">
              {pou.nameMi}
            </h2>
            <ul className="rpt-svc-list">
              {subs.map((s) => {
                const services = SERVICE_CONTRIBUTION[s.subdomainId] ?? [];
                return (
                  <li key={s.subdomainId} className="rpt-svc-row">
                    <div className="rpt-svc-row__head">
                      <h3 className="rpt-svc-row__name">
                        {labels.subdomain(s.subdomainId)}
                      </h3>
                      {s.assessedExit > 0 && (
                        <span
                          className="rpt-svc-row__stat"
                          title={t("reports.services.protectiveAtExit")}
                        >
                          {pct(s.exitProportion.protective)}{" "}
                          <span className="rpt-svc-row__stat-label">
                            {t("reports.services.protectiveShort")}
                          </span>
                        </span>
                      )}
                    </div>
                    <p className="rpt-svc-row__toc">
                      {labels.toc(s.subdomainId)}
                    </p>
                    {services.length > 0 ? (
                      <ul className="rpt-svc-chips">
                        {services.map((svc) => (
                          <li key={svc} className="rpt-svc-chip">
                            {svc}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="rpt-svc-row__none">
                        {t("reports.services.none")}
                      </p>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

export default ServicesView;
